/* eslint-disable no-param-reassign */
import { Accordion } from 'semantic-ui-react'
import filter from 'lodash/filter'
import React, { useState } from 'react'
import {
  CONDITION,
  DISPLAY_MODE,
  ROLES
} from '../../../constant'
import {
  condition as conditionUtils,
  conditionGroupTitle,
  filterWithStatus,
  groupByAddressed,
  hasConditionsOrActivities,
  orderConditionsByHccConfidenceDescending
} from '../../common/conditionUtils'
import { groupByHccCode } from '../../common/hccUtils'
import {
  encounterConditionGroups,
  // encounterConditionGroups,
  encounterConditionGroupsEnabled,
  hccGroupingEnabled
} from '../../common/currentUserUtils'
import ConditionHeader from '../common/conditionHeader'
import PatientSnapshotCondition from './patientSnapshotCondition'
import PatientSnapshotConditionHccGroup from './patientSnapshotConditionHccGroup'
import '../detail.css'
import {
  DisplayConditions,
  EncounterConditionDisplay,
  Group,
  PatientType,
  User
} from '../../authenticated/types'
import previousEncounters from './previousEncounters'
import If from '../../common/if'
import { ConditionType } from '../../patient/patient'

/**
 * Provides a component that renders the Patient Snapshot encounter conditions.
 *
 * The Patient Snapshot view of conditions is very close to the Clinical Admin view
 * of encounter conditions with the exception that the conditions are 100% read
 * only
 *
 * @param mode View mode. Options: "smart", null. Default: null
 * @param encounterDetail Encounter detail entity
 * @param conditionGroups Optional condition groups to display; overrides tenant property
 * @param activeConditionId Active condition ID (default: null). This is the
 *        last condition or group that the user expanded
 * @param onConditionAction Callback for condition action.
 *        Arguments: action, condition
 */

type Props = {
  activeConditionId: string | null
  currentUser: User
  mode: keyof typeof DISPLAY_MODE | null
  onConditionAction: (
    action: keyof typeof CONDITION.ACTION,
    condition: EncounterConditionDisplay | string
  ) => void
  encounterDetail: PatientType
  conditionGroups?: keyof typeof CONDITION.TYPE | undefined
}

function PatientSnapshotEncounterConditions(
  props: Props
): JSX.Element {
  const {
    onConditionAction,
    activeConditionId,
    currentUser,
    encounterDetail,
    conditionGroups
  } = props

  const [ activeConditionIdState ] = useState<string | null>(activeConditionId)

  const groupConditions = (conditions: EncounterConditionDisplay[]) => {
    if (hccGroupingEnabled(currentUser)) return groupByHccCode(conditions)
    return groupByAddressed(conditions)
  }

  const filterConditions = (
    conditions: EncounterConditionDisplay[],
    conditionStatusCode: keyof typeof CONDITION.TYPE
  ): EncounterConditionDisplay[] => {
    const ignoreCodes = [ CONDITION.CODE.REMOVED ]
    return orderConditionsByHccConfidenceDescending(
      filterWithStatus(ROLES.PATIENT_SNAPSHOT, conditions, conditionStatusCode, ignoreCodes)
    )
  }

  const sortConditionGroups = (groups: Group[]) => {
    const withHcc = groups.filter(g => g.hccCode !== null)
    const withHccAddressed = withHcc.filter(g => g.addressed === true)
    const withHccNotAddressed = withHcc.filter(g => g.addressed === false)
    const withoutHcc = groups.filter(g => g.hccCode === null)
    const withoutHccAddressed = withoutHcc.filter(g => g.addressed === true)
    const withoutHccNotAddressed = withoutHcc.filter(g => g.addressed === false)
    return withHccNotAddressed
      .concat(withoutHccNotAddressed)
      .concat(withHccAddressed)
      .concat(withoutHccAddressed)
  }

  const sortConditions = (conditions: EncounterConditionDisplay[]) => {
    const addressed = filter(conditions, condition =>
      conditionUtils.isConditionAddressed(condition))
    const notAddressed = filter(
      conditions,
      condition => !conditionUtils.isConditionAddressed(condition)
    )
    return notAddressed.concat(addressed)
  }

  const renderHccGroup = (
    group: Group,
    groupTitle: ConditionType
  ): JSX.Element => (
    <PatientSnapshotConditionHccGroup
      key={ `${group.hccCode}-${groupTitle}` }
      hccCode={ group.hccCode }
      hccDescription={ group.hccDescription }
      activeConditionId={ activeConditionIdState }
      conditionGroupTitle={ groupTitle }
      conditions={ group.conditions }
      onConditionAction={ onConditionAction }
    />
  )

  const renderUngrouped = (group: Group) =>
    group.conditions.map((condition, conditionIndex) => (
      <PatientSnapshotCondition
        key={ condition.id }
        condition={ condition }
        displayIndex={ conditionIndex }
        onConditionAction={ onConditionAction }
        hccChild={ false }
      />
    ))

  const renderConditions = (displayConditions: DisplayConditions[]) => {
    const key = encounterDetail.encounter.id
    return (
      <div key={ key }>
        { displayConditions.map(
          (display: DisplayConditions, displayIndex: number) => {
            display.hasInsightsToShow = hasConditionsOrActivities(
              display.conditions,
              currentUser.type
            )
            return (
              <div key={ display.title }>
                { /* SHAWN SHOULD THIS BE POPULATED?????????????????????? */ }
                <ConditionHeader
                  conditions={ [] }
                  title={ display.title }
                  displayIndex={ displayIndex }
                />
                <If isTrue={ display.hasInsightsToShow }>
                  <Accordion styled fluid>
                    { display.conditions.map((group: Group) => {
                      if (
                        group.hccCode === null &&
                        group.conditions.length > 0
                      ) {
                        return renderUngrouped(group)
                      }
                      if (
                        group.hccCode !== null &&
                        group.conditions.length > 0
                      ) {
                        return (
                          renderHccGroup(
                            group,
                            display.title as ConditionType
                          )
                        )
                      }
                      return null
                    }) }
                  </Accordion>
                </If>
                <If isTrue={ !display.hasInsightsToShow }>
                  <Accordion styled fluid>
                    <Accordion.Content>
                      <span>{ `No ${display.title} to show at this time.` }</span>
                    </Accordion.Content>
                  </Accordion>
                </If>
              </div>
            )
          }
        ) }
      </div>
    )
  }

  const displayConditions: DisplayConditions[] = []
  const groupsEnabled = encounterConditionGroupsEnabled(currentUser)
  const conditionGroupsFn = encounterConditionGroups(
    currentUser,
    conditionGroups
  )

  conditionGroupsFn.forEach(conditionStatusCode => {
    if (groupsEnabled[conditionStatusCode] === true) {
      displayConditions.push({
        title: conditionGroupTitle(
          conditionStatusCode as keyof typeof CONDITION.TYPE
        ),
        conditions: sortConditionGroups(
          groupConditions(
            sortConditions(
              filterConditions(
                encounterDetail.conditions,
                conditionStatusCode as keyof typeof CONDITION.TYPE
              )
            )
          )
        )
      })
    }
  })

  return renderConditions(displayConditions)
}

export default PatientSnapshotEncounterConditions
